import React, { useState } from "react";
import * as StateLib from "../Utils/StateLib";
import ViewRuleEditor from "@/appComponents/Blocks/Modals/ViewRuleEditor/ViewRuleEditor";
import ViewLibraryModal from "@/appComponents/Blocks/Modals/ViewLibraryModal/ViewLibraryModal";
import { useRouteState } from "@/hooks/useRouteState";
import { useChartDocumentContext } from "./ChartDocumentProvider";
import { SubscribeExternalModuleReactAPI } from "@coreapp/main";

const ViewManagerContext = React.createContext();

const ViewManagerProvider = ({ children }) => {
  const [viewMgr, setViewMgr] = useState({
    state: StateLib.GetWSMgr() && StateLib.GetWSMgr().GetChartTemplatesMgr()
  });

  const { view: viewParam } = useRouteState();
  const { chartDocument } = useChartDocumentContext();

  const [curRuleData, setCurRuleData] = useState(null);
  const [showRuleEditor, setShowRuleEditor] = useState(false);
  const [showViewLibrary, setShowViewLibrary] = useState(false);

  const onCloseRuleEditor = () => {
    setCurRuleData(null);
    setShowRuleEditor(false);
  };

  const ruleContextValue = {
    theData: curRuleData,
    onCloseRuleEditor,
    showRuleEditor
  };

  const handleRuleEditorEvent = ruleData => {
    setCurRuleData(ruleData);
    setShowRuleEditor(true);
  };

  React.useEffect(() => {
    SubscribeExternalModuleReactAPI(setViewMgr, "viewmgr");
  }, []);

  React.useEffect(() => {
    if (viewMgr && viewMgr["state"] != null) {
      viewMgr["state"].handleRule = handleRuleEditorEvent;
    }
  }, [viewMgr, handleRuleEditorEvent]);

  // URL → coreapp: ?view={templateId} pins the active view. Fires on deep link,
  // back/forward nav, and doc reload. Guarded so we never call DoSetView before
  // the canvas has a current subchart — DoSetView → AutoFormat dereferences it
  // and would crash during plan loads or any mid-mount render.
  //
  // Per-view access is also gated here (defense-in-depth alongside
  // useUrlSecurityGate's URL-strip): if the ?view= passenger resolves to a
  // view the user isn't allowed to see, we must NOT call DoSetView — the
  // gate will strip the param on its next pass, but until then the
  // provider refuses to apply forbidden state. canAccessView returns true
  // while templates are still loading, so valid cold-start URLs survive.
  React.useEffect(() => {
    if (viewParam == null) return;
    const wsMgr = StateLib.GetWSMgr();
    const doc = wsMgr?.GetDoc();
    if (!doc) return;
    const canApplyView = wsMgr?.fPermissions?.canApplyView(viewParam);
    if (!canApplyView) return;
    const canvasPanel = wsMgr?.fCanvasPanel;
    if (!canvasPanel) return;
    const scm = canvasPanel.GetSubChartMgr?.();
    if (!scm?.GetCurrentSubChart?.()) return;
    const activeTemplate = doc.GetActiveTemplate?.();
    if (!activeTemplate) return;
    const currentViewId = activeTemplate.GetTemplateID?.();
    if (String(currentViewId) === String(viewParam)) return;
    wsMgr.DoSetView(viewParam);
    StateLib.ReRenderCanvas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewParam, chartDocument]);

  return (
    <ViewManagerContext.Provider
      value={{
        viewMgr,
        setViewMgr,
        ruleContextValue,
        setShowViewLibrary
      }}
    >
      <ViewRuleEditor />
      <ViewLibraryModal open={showViewLibrary} onClose={() => setShowViewLibrary(false)} />
      {children}
    </ViewManagerContext.Provider>
  );
};

export const useViewManagerContext = () => React.useContext(ViewManagerContext);

export default ViewManagerProvider;
